// Config for the "Valuation Report Details" tab on the New Job form.
// Mirrors the bank valuation report structure. All values stored under job.valuation_details.

const YES_NO = ["Yes", "No"];

// Straight-line depreciation: depreciated rate = replacement rate × future life ÷ (age + future life).
// Net value = plinth area × depreciated rate.
export function computeBuildingBlockRow(r) {
  const n = (v) => { const x = parseFloat(String(v ?? "").replace(/,/g, "")); return Number.isFinite(x) ? x : 0; };
  const cr = n(r.const_rate), age = n(r.age), fl = n(r.future_life), area = n(r.area);
  let dep = "";
  if (cr) {
    const total = age + fl;
    dep = total > 0 ? Math.round((cr * fl) / total) : Math.round(cr);
  }
  const amount = dep !== "" && area ? Math.round(area * dep) : "";
  return { dep_rate: dep, amount };
}

export const VALUATION_SECTIONS = [
  {
    id: "owner_general",
    title: "Owner & General Info",
    fieldScan: true,
    fields: [
      { key: "field_lat", label: "Field Checked Latitude" },
      { key: "field_lon", label: "Field Checked Longitude" },
      { key: "val_id", label: "VAL ID / Ref No.", readOnly: true, placeholder: "Auto-generated on save", hint: "Format: AAK/YYYY/MM/001" },
      { key: "owner_name", label: "Owner Name" },
      { key: "owner_father_name", label: "Father's / Spouse Name" },
      { key: "owner_address", label: "Owner Address", full: true, type: "textarea" },
      { key: "owner_contact", label: "Owner Contact No." },
      { key: "date_of_inspection", label: "Date of Inspection", type: "date" },
      { key: "date_of_valuation", label: "Date of Valuation", type: "date" },
      { key: "documents_produced", label: "Documents Produced for Perusal", full: true, type: "textarea" },
      { key: "property_description", label: "Brief Description of Property", full: true, type: "textarea" },
      { key: "tenure", label: "Tenure", type: "select", options: ["Freehold", "Leasehold"] },
    ],
  },
  {
    id: "location",
    title: "Property Location & Identifiers",
    fields: [
      { key: "village", label: "Village" },
      { key: "pc_no", label: "P.C. No." },
      { key: "bandobast_no", label: "Bandobast No." },
      { key: "ph_no", label: "P.H. No." },
      { key: "khasra_no", label: "Khasra No." },
      { key: "total_rakba", label: "Total Rakba (Hect)" },
      { key: "total_land_area_sqft", label: "Total Land Area (Sq Ft)", type: "number" },
      { key: "ward_no", label: "Ward No. / Name" },
      { key: "rnm", label: "RNM (Revenue Niriksak Mandal)" },
      { key: "tehsil", label: "Tehsil" },
      { key: "district", label: "District" },
      { key: "state", label: "State" },
      { key: "pincode", label: "Pincode" },
      { key: "country", label: "Country" },
      { key: "latitude", label: "Latitude" },
      { key: "longitude", label: "Longitude" },
      { key: "door_no", label: "Door No." },
      { key: "landmark", label: "Landmark of Property" },
      { key: "postal_address", label: "Postal Address of Property", full: true, type: "textarea" },
    ],
  },
  {
    id: "area_classification",
    title: "Area Classification",
    fields: [
      { key: "is_residential_area", label: "Residential Area", type: "select", options: YES_NO },
      { key: "is_commercial_area", label: "Commercial Area", type: "select", options: YES_NO },
      { key: "is_industrial_area", label: "Industrial Area", type: "select", options: YES_NO },
      { key: "income_class", label: "Income Class", type: "select", options: ["Upper Class", "Middle Class", "Poor Class"] },
      { key: "urban_classification", label: "Urban / Semi-Urban / Rural", type: "select", options: ["Urban", "Semi Urban", "Rural"] },
      { key: "civic_body", label: "Civic Body", type: "select", options: ["Corporation", "Municipality", "Village Panchayat"] },
      { key: "govt_enactment", label: "Covered Under Govt. Enactment", type: "select", options: YES_NO },
      { key: "agricultural_conversion", label: "Agricultural Land Conversion / Diversion" },
    ],
  },
  {
    id: "boundaries",
    title: "Boundaries (As per Deed / Actual)",
    fields: [
      { key: "boundary_north_deed", label: "North — As per Deed" },
      { key: "boundary_north_actual", label: "North — Actual" },
      { key: "boundary_south_deed", label: "South — As per Deed" },
      { key: "boundary_south_actual", label: "South — Actual" },
      { key: "boundary_east_deed", label: "East — As per Deed" },
      { key: "boundary_east_actual", label: "East — Actual" },
      { key: "boundary_west_deed", label: "West — As per Deed" },
      { key: "boundary_west_actual", label: "West — Actual" },
    ],
  },
  {
    id: "additional_boundaries",
    title: "Additional Boundaries",
    rows: true,
    rowKey: "additional_boundaries",
    columns: [
      { key: "side", label: "Side / Direction" },
      { key: "deed", label: "As per Deed" },
      { key: "actual", label: "Actual" },
    ],
  },
  {
    id: "dimensions",
    title: "Dimensions (As per Deed / Actual)",
    fields: [
      { key: "dim_north_deed", label: "North — As per Deed" },
      { key: "dim_north_actual", label: "North — Actual" },
      { key: "dim_south_deed", label: "South — As per Deed" },
      { key: "dim_south_actual", label: "South — Actual" },
      { key: "dim_east_deed", label: "East — As per Deed" },
      { key: "dim_east_actual", label: "East — Actual" },
      { key: "dim_west_deed", label: "West — As per Deed" },
      { key: "dim_west_actual", label: "West — Actual" },
    ],
  },
  {
    id: "site_characteristics",
    title: "Characteristics of the Site",
    fields: [
      { key: "extent_of_site", label: "Extent of the Site" },
      { key: "road_widening", label: "Road Widening / Other" },
      { key: "extent_considered", label: "Extent Considered for Valuation" },
      { key: "occupancy", label: "Occupied by Owner / Tenant", full: true },
      { key: "locality_classification", label: "Classification of Locality" },
      { key: "surrounding_development", label: "Development of Surrounding Areas" },
      { key: "flooding_possibility", label: "Possibility of Flooding", type: "select", options: YES_NO },
      { key: "civic_amenities_feasibility", label: "Feasibility to Civic Amenities" },
      { key: "land_level", label: "Level of Land / Topography" },
      { key: "land_shape", label: "Shape of Land" },
      { key: "land_use_type", label: "Type of Use" },
      { key: "usage_restriction", label: "Any Usage Restriction", type: "select", options: YES_NO },
      { key: "town_planning_layout", label: "In Town Planning Approved Layout?" },
      { key: "plot_type", label: "Corner / Intermittent Plot", type: "select", options: ["Corner Plot", "Intermittent Plot"] },
      { key: "road_facilities", label: "Road Facilities" },
      { key: "road_type", label: "Type of Road" },
      { key: "road_width", label: "Width of Road" },
      { key: "land_locked", label: "Land-Locked Land?" },
      { key: "water_potential", label: "Water Potential" },
      { key: "sewerage_system", label: "Underground Sewerage System" },
      { key: "power_supply", label: "Power Supply Availability" },
      { key: "site_advantages", label: "Advantages of the Site", full: true, type: "textarea" },
      { key: "general_remarks", label: "General Remarks", full: true, type: "textarea" },
    ],
  },
  {
    id: "land_valuation",
    title: "Part A — Valuation of Land",
    fields: [
      { key: "plot_size_ns", label: "Plot Size — North & South" },
      { key: "plot_size_ew", label: "Plot Size — East & West" },
      { key: "land_area_sqft", label: "Land Area (Sq Ft)", type: "number" },
      { key: "land_rate_per_sqft", label: "Rate / Sq Ft (₹)", type: "number" },
      { key: "land_value", label: "Land Value (₹)", computed: true, hint: "Auto = Land Area × Rate / Sq Ft" },
      { key: "total_extent", label: "Total Extent of Plot" },
      { key: "prevailing_market_rate", label: "Prevailing Market Rate / Sq Ft (₹)", type: "number" },
      { key: "guideline_rate_per_sqft", label: "Guideline Rate / Sq Ft (₹)", type: "number" },
      { key: "guideline_value", label: "Guideline Value (₹)", computed: true, hint: "Auto = Land Area × Guideline Rate" },
      { key: "adopted_rate", label: "Assessed / Adopted Rate (₹)", type: "number" },
      { key: "estimated_land_value", label: "Estimated Value of Land (₹)", computed: true, hint: "Auto = Prevailing Market Rate × Land Area" },
    ],
  },
  {
    id: "extra_land_details",
    title: "Additional Land / Parcel Details",
    rows: true,
    rowKey: "extra_land_details",
    computeValue: { areaKey: "area", rateKey: "rate", valueKey: "amount" },
    columns: [
      { key: "description", label: "Particulars" },
      { key: "area", label: "Area (Sq Ft)" },
      { key: "rate", label: "Rate / Sq Ft (₹)" },
      { key: "amount", label: "Value (₹)", computed: true },
    ],
  },
  {
    id: "comparable_sales",
    title: "PART A — Comparable Sale Evidence (Market Rate)",
    rows: true,
    rowKey: "comparable_sales",
    columns: [
      { key: "property", label: "Property / Location" },
      { key: "area", label: "Area (Sq Ft)" },
      { key: "rate", label: "Rate ₹/Sq Ft" },
      { key: "date", label: "Date" },
      { key: "source", label: "Source / Remarks" },
    ],
  },
  {
    id: "building_valuation",
    title: "Part B — Valuation of Building",
    fields: [
      { key: "building_type", label: "Type of Building" },
      { key: "construction_type", label: "Type of Construction" },
      { key: "year_of_construction", label: "Year of Construction" },
      { key: "building_age", label: "Age of Building" },
      { key: "future_life", label: "Expected Future Life" },
      { key: "floors_height", label: "No. of Floors & Height" },
      { key: "plinth_area", label: "Plinth Area (floorwise)" },
      { key: "exterior_condition", label: "Exterior Condition", type: "select", options: ["Excellent", "Good", "Normal", "Poor", "NA"] },
      { key: "interior_condition", label: "Interior Condition", type: "select", options: ["Excellent", "Good", "Normal", "Poor", "NA"] },
    ],
  },
  {
    id: "building_blocks",
    title: "Part B — Building Blocks (Value = Area × Depreciated Rate)",
    rows: true,
    rowKey: "building_blocks",
    computeRow: (r) => computeBuildingBlockRow(r),
    columns: [
      { key: "description", label: "Building / Block" },
      { key: "area", label: "Plinth Area (Sq Ft)" },
      { key: "const_rate", label: "Const. Rate ₹/Sq Ft" },
      { key: "age", label: "Age (yrs)" },
      { key: "future_life", label: "Future Life (yrs)" },
      { key: "dep_rate", label: "Deprec. Rate (₹)", computed: true },
      { key: "amount", label: "Value (₹)", computed: true },
    ],
  },
  {
    id: "spec_items",
    title: "PART B — Floor-wise Construction Specification",
    itemGroup: true,
    itemKey: "spec_items",
    valueField: "detail",
    items: ["Foundation", "Basement", "Superstructure", "Joinery (doors / windows)", "RCC works", "Plastering", "Flooring, skirting, dadoing", "Special finish (marble / granite)", "Roofing incl. weatherproofing", "Drainage", "Compound wall", "Electrical installation", "Plumbing installation"],
  },
  {
    id: "valuation_summary",
    title: "Valuation Summary",
    fields: [
      { key: "part_a_value", label: "Part A — Land Value (₹)", computed: true, hint: "Auto = Estimated Land Value + Additional Parcels" },
      { key: "part_b_value", label: "Part B — Building Value (₹)", computed: true, hint: "Auto = Σ Building Blocks" },
      { key: "fmv", label: "Total (A + B) = Fair Market Value — FMV (₹)", computed: true, full: true, hint: "Auto = Part A + Part B" },
      { key: "realisable_value_90", label: "Realisable Value @ 90% (₹)", computed: true, hint: "Auto = 90% of FMV" },
      { key: "distress_value_80", label: "Distress Value @ 80% (₹)", computed: true, hint: "Auto = 80% of FMV" },
      { key: "guideline_value_summary", label: "Guideline Value — GV (₹)", computed: true, hint: "Auto = Land Area × Guideline Rate" },
      { key: "market_value_words", label: "Market Value in Words", full: true, readOnly: true, hint: "Auto = Fair Market Value (FMV) in words" },
      { key: "valuation_method", label: "Method of Valuation" },
      { key: "report_status", label: "Report Status", type: "select", options: ["Positive", "Negative"] },
      { key: "valuation_place", label: "Place of Valuation" },
      { key: "valuation_date_summary", label: "Valuation Date", type: "date" },
      { key: "report_map_zoom", label: "Report Map Zoom", type: "select", options: ["Street", "Neighbourhood", "Area"], hint: "Street = closest view; Area = widest" },
    ],
  },
  {
    id: "part_c_items",
    title: "PART C — Extra Items (Values)",
    itemGroup: true, itemKey: "part_c_items", valueField: "amount", money: true,
    items: ["Portico", "Ornamental front door", "Sit-out / verandah with steel grills", "Overhead water tank", "Extra steel / collapsible gates", "Extra RCC / weather shade", "Open staircase", "Bore well / tube well"],
  },
  {
    id: "part_d_items",
    title: "PART D — Amenities (Values)",
    itemGroup: true, itemKey: "part_d_items", valueField: "amount", money: true,
    items: ["Wardrobes", "Glazed / designer tiles", "Extra sinks and bath tubs", "Marble / granite / Kota flooring", "Interior decorations", "Architectural elevation works", "Panelling works", "Aluminium / UPVC works", "False ceiling", "Modular kitchen"],
  },
  {
    id: "part_e_items",
    title: "PART E — Miscellaneous (Values)",
    itemGroup: true, itemKey: "part_e_items", valueField: "amount", money: true,
    items: ["Separate toilet room", "Separate lumber room", "Separate water tank / sump", "Trees, gardening & landscaping"],
  },
  {
    id: "part_f_items",
    title: "PART F — Services (Values)",
    itemGroup: true, itemKey: "part_f_items", valueField: "amount", money: true,
    items: ["Water supply arrangements", "Drainage arrangements", "Compound wall", "C.B. deposits, fittings, etc.", "Pavement / paving"],
  },
  {
    id: "part_c_declarations",
    title: "Mandatory Declarations (Annexure-I)",
    declarations: true,
    fields: [
      { key: "decl_1", type: "checkbox", full: true, label: "The information furnished in my valuation report is true and correct to the best of my knowledge and belief; I have made an impartial and true valuation of the property." },
      { key: "decl_2", type: "checkbox", full: true, label: "I have no direct or indirect interest in the property valued." },
      { key: "decl_3", type: "checkbox", full: true, label: "I have personally inspected the property; the field surveyor has visited and verified it. The work is not sub-contracted to any other valuer and was carried out by me." },
      { key: "decl_4", type: "checkbox", full: true, label: "I have not been convicted of any offence and sentenced to a term of imprisonment." },
      { key: "decl_5", type: "checkbox", full: true, label: "I have not been found guilty of misconduct in my professional capacity." },
      { key: "decl_6", type: "checkbox", full: true, label: "I have read the IBA Handbook on Policy, Standards and Procedure for Real Estate Valuation, 2011 and this report conforms to the standards enshrined therein." },
      { key: "decl_7", type: "checkbox", full: true, label: "I have read the International Valuation Standards (IVS) and this report conforms to the standards as applicable to the respective asset class." },
      { key: "decl_8", type: "checkbox", full: true, label: "I abide by the Model Code of Conduct for empanelment of valuers in the Bank." },
      { key: "decl_9", type: "checkbox", full: true, label: "I am registered under Section 34 AB of the Wealth Tax Act, 1957." },
      { key: "decl_10", type: "checkbox", full: true, label: "I am the proprietor / partner / authorised official of the firm who is competent to sign this valuation report." },
      { key: "valuer_name", label: "Valuer Name" },
      { key: "valuer_reg_no", label: "Valuer Registration No." },
      { key: "field_surveyor_name", label: "Field Surveyor Name" },
      { key: "inspection_with", label: "Inspected With (Owner / Representative)" },
      { key: "inspection_contact", label: "Inspection Contact No." },
      { key: "restrictions_on_use", label: "Restrictions on Use of Report", full: true, type: "textarea" },
      { key: "annexure_remarks", label: "Annexure / Caveat Remarks", full: true, type: "textarea" },
    ],
  },
  {
    id: "construction_details",
    title: "Area / Construction Details",
    rows: true,
    rowKey: "construction_details",
    columns: [
      { key: "usage_category", label: "Usage Category" },
      { key: "construction_type", label: "Construction Type" },
      { key: "size", label: "Size" },
      { key: "unit", label: "Unit" },
    ],
  },
  {
    id: "locational_intelligence",
    title: "Locational Intelligence",
    rows: true,
    rowKey: "locational_intelligence",
    columns: [
      { key: "landmark_nature", label: "Landmark Nature" },
      { key: "nearest_landmark", label: "Nearest Landmark" },
      { key: "distance", label: "Distance" },
    ],
  },
];
